import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { formatearFecha, fechaLocal } from "../utils/fechas";

const font = "Montserrat, sans-serif";

const DAYS_ES   = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const DAYS_FULL = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const SERVICE_LABELS = {
  jardinero: "Jardinería", plomeros: "Plomería", electricista: "Electricista",
  gasista: "Gasista", albanileria: "Albañilería", pintura: "Pintores",
  cerrajeria: "Cerrajería", "aire-acondicionado": "Aire Acond.", tecnologia: "Tecnología",
  mudanzas: "Mudanzas", limpieza: "Limpieza", carpinteria: "Carpintería",
  arquitectura: "Arquitectura", belleza_personal: "Belleza Personal",
  control_plagas: "Control de Plagas", cuidado_mayores: "Cuidado de Mayores",
  cuidado_ninos: "Cuidado de Niños", decoradores: "Decoradores",
  entrenamiento: "Entrenamiento", herrero: "Herrero", infantiles: "Infantiles",
  instalador_general: "Instalador General", mascotas: "Mascotas",
  mecanica_automotriz: "Mecánica Automotriz", piletas: "Piletas",
  seguridad: "Seguridad", servicio_domestico: "Serv. Doméstico", tapicero: "Tapicero",
};

const STATUS_CONFIG = {
  scheduled:   { label: "Agendado",   color: "#163244", bg: "rgba(22,50,68,0.08)",   dot: "#163244" },
  in_progress: { label: "En curso",   color: "#b06a00", bg: "rgba(176,106,0,0.08)",  dot: "#E7A432" },
  completed:   { label: "Completado", color: "#1b7f4e", bg: "rgba(27,127,78,0.08)",  dot: "#1b7f4e" },
  cancelled:   { label: "Cancelado",  color: "#c0392b", bg: "rgba(192,57,43,0.08)",  dot: "#c0392b" },
};

const BLOCK_REASONS = ["Personal", "Vacaciones", "Feriado", "Enfermedad", "Capacitación", "Otro"];

function getMondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function formatDate(date) { return new Date(date).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
function formatTime(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}
function isSameDay(d1, d2) {
  const a = new Date(d1), b = new Date(d2);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function isToday(date) { return isSameDay(date, new Date()); }

// ─── Input / label styles ──────────────────────────────────────────────────────
const inputSx = { padding: "9px 12px", borderRadius: 9, border: "1.5px solid rgba(0,0,0,0.12)", fontSize: 13, fontFamily: font, color: "#163244", background: "#fff", width: "100%", boxSizing: "border-box", outline: "none" };
const labelSx = { fontSize: 10, fontWeight: 700, color: "#7a93a4", display: "block", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };

// ─── Job Card (celda de la grilla) ────────────────────────────────────────────
function JobCard({ job, onClick }) {
  const cfg   = STATUS_CONFIG[job.status] || STATUS_CONFIG.scheduled;
  const label = SERVICE_LABELS[job.service_key] || job.service_key;
  const time  = formatTime(job.scheduled_start);

  return (
    <div onClick={() => onClick(job)} style={{
      background: cfg.bg, borderLeft: `3px solid ${cfg.dot}`,
      borderRadius: 7, padding: "6px 8px", marginBottom: 4,
      cursor: "pointer", fontSize: 11, transition: "transform 0.1s",
    }}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
      onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
        <span style={{ fontWeight: 700, color: cfg.color, fontSize: 9, letterSpacing: "0.05em" }}>{cfg.label.toUpperCase()}</span>
      </div>
      <div style={{ fontWeight: 700, color: "#163244", fontSize: 11, marginBottom: 1 }}>{label}</div>
      <div style={{ color: "#7a93a4", fontSize: 10 }}>👤 {job.client_name}</div>
      {time && !job.all_day && <div style={{ color: "#aab8c2", fontSize: 9, marginTop: 2 }}>🕐 {time}hs</div>}
      {job.all_day && <div style={{ color: "#aab8c2", fontSize: 9, marginTop: 2 }}>Todo el día</div>}
    </div>
  );
}

function BlockChip({ block, onClick }) {
  return (
    <div onClick={() => onClick(block)} style={{
      background: "rgba(192,57,43,0.07)", borderLeft: "3px solid #c0392b",
      borderRadius: 7, padding: "6px 8px", marginBottom: 4, cursor: "pointer", fontSize: 11,
    }}>
      <div style={{ fontWeight: 700, color: "#c0392b", fontSize: 9, marginBottom: 2 }}>🚫 BLOQUEADO</div>
      <div style={{ color: "#c0392b", fontWeight: 600, fontSize: 10 }}>{block.reason}</div>
    </div>
  );
}

// ─── Job Modal ────────────────────────────────────────────────────────────────
function JobModal({ job, onClose, onSave, onStatusChange }) {
  const [form, setForm] = useState({
    scheduled_start:      job.scheduled_start ? new Date(job.scheduled_start).toISOString().slice(0, 16) : "",
    scheduled_end:        job.scheduled_end   ? new Date(job.scheduled_end).toISOString().slice(0, 16)   : "",
    all_day:              job.all_day ?? true,
    notes:                job.notes || "",
    estimated_duration_hs: job.estimated_duration_hs || "",
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [tab,     setTab]     = useState("info");

  const cfg          = STATUS_CONFIG[job.status] || STATUS_CONFIG.scheduled;
  const serviceLabel = SERVICE_LABELS[job.service_key] || job.service_key;
  const NEXT_STATUSES = {
    scheduled:   [{ value: "in_progress", label: "▶ Iniciar trabajo" }],
    in_progress: [{ value: "completed",   label: "✓ Marcar completado" }],
  };
  const nextActions = NEXT_STATUSES[job.status] || [];

  const handleSave = async () => {
    setError(""); setLoading(true);
    try {
      const body = { all_day: form.all_day, notes: form.notes || null, estimated_duration_hs: form.estimated_duration_hs || null };
      if (!form.all_day && form.scheduled_start) body.scheduled_start = new Date(form.scheduled_start).toISOString();
      if (!form.all_day && form.scheduled_end)   body.scheduled_end   = new Date(form.scheduled_end).toISOString();
      if (form.all_day && form.scheduled_start) {
        const d = new Date(form.scheduled_start); d.setHours(9, 0, 0, 0); body.scheduled_start = d.toISOString();
      }
      await api.put(`/providers/calendar/jobs/${job.id}`, body);
      onSave(); onClose();
    } catch (err) { setError(err.response?.data?.error || "Error al guardar"); }
    finally { setLoading(false); }
  };

  const handleStatus = async (newStatus) => {
    setLoading(true);
    try { await api.patch(`/providers/calendar/jobs/${job.id}/status`, { status: newStatus }); onStatusChange(); onClose(); }
    catch (err) { setError(err.response?.data?.error || "Error al cambiar estado"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, width: "min(500px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        {/* Header dark */}
        <div style={{ background: "#163244", padding: "20px 24px", borderRadius: "18px 18px 0 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 90% 20%, rgba(231,164,50,0.15) 0%, transparent 55%)", pointerEvents: "none" }} />
          <div style={{ zIndex: 1 }}>
            <div style={{ color: "#E7A432", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>{serviceLabel.toUpperCase()}</div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 17, marginBottom: 6 }}>{job.client_name}</div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: cfg.bg, borderRadius: 20, padding: "3px 10px" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color }}>{cfg.label}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(0,0,0,0.07)" }}>
          {[["info","Información"],["edit","Editar"]].map(([t, lbl]) => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "12px", border: "none", background: tab === t ? "#fff" : "#f8fafb", borderBottom: tab === t ? "2px solid #163244" : "2px solid transparent", color: tab === t ? "#163244" : "#7a93a4", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer", letterSpacing: "0.04em" }}>
              {lbl.toUpperCase()}
            </button>
          ))}
        </div>

        <div style={{ padding: "20px 24px" }}>
          {tab === "info" && (
            <div>
              {/* Contacto */}
              <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                <div style={labelSx}>Contacto del cliente</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}><span>👤</span><span style={{ fontWeight: 700, color: "#163244", fontSize: 13 }}>{job.client_name}</span></div>
                  {job.client_phone && <div style={{ display: "flex", gap: 10, alignItems: "center" }}><span>📱</span><a href={`tel:${job.client_phone}`} style={{ color: "#163244", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>{job.client_phone}</a></div>}
                  {job.client_email && <div style={{ display: "flex", gap: 10, alignItems: "center" }}><span>✉️</span><a href={`mailto:${job.client_email}`} style={{ color: "#163244", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>{job.client_email}</a></div>}
                  {job.address      && <div style={{ display: "flex", gap: 10, alignItems: "center" }}><span>📍</span><span style={{ color: "#7a93a4", fontSize: 12 }}>{job.address}</span></div>}
                </div>
              </div>

              {/* Fecha */}
              <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                <div style={labelSx}>Fecha y hora</div>
                <div style={{ fontSize: 13, color: "#163244", fontWeight: 600 }}>
                  {job.scheduled_start ? (job.all_day ? formatearFecha(job.scheduled_start) : `${formatDate(job.scheduled_start)} — ${formatTime(job.scheduled_start)}hs`) : "Sin fecha confirmada"}
                </div>
              </div>

              {/* Notas */}
              {job.notes && (
                <div style={{ background: "rgba(231,164,50,0.07)", border: "1px solid rgba(231,164,50,0.3)", borderRadius: 9, padding: "12px 14px", marginBottom: 14, fontSize: 13, color: "#163244", lineHeight: 1.6 }}>
                  📝 {job.notes}
                </div>
              )}

              {/* Acciones */}
              {nextActions.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {nextActions.map(action => (
                    <button key={action.value} onClick={() => handleStatus(action.value)} disabled={loading} style={{ padding: "12px", borderRadius: 50, border: "none", background: action.value === "completed" ? "#1b7f4e" : "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      {loading ? "..." : action.label}
                    </button>
                  ))}
                  {job.status !== "completed" && job.status !== "cancelled" && (
                    <button onClick={() => handleStatus("cancelled")} disabled={loading} style={{ padding: "10px", borderRadius: 50, border: "1.5px solid rgba(192,57,43,0.3)", background: "transparent", color: "#c0392b", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                      Cancelar trabajo
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "edit" && (
            <div>
              {/* Todo el día toggle */}
              <div style={{ marginBottom: 14 }}>
                <label style={labelSx}>Tipo de agenda</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[{ val: true, label: "Todo el día" }, { val: false, label: "Con horario" }].map(opt => (
                    <button key={String(opt.val)} onClick={() => setForm(f => ({ ...f, all_day: opt.val }))} style={{ flex: 1, padding: "9px", borderRadius: 9, cursor: "pointer", border: "1.5px solid", borderColor: form.all_day === opt.val ? "#163244" : "rgba(0,0,0,0.12)", background: form.all_day === opt.val ? "#163244" : "#fff", color: form.all_day === opt.val ? "#fff" : "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13 }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelSx}>{form.all_day ? "Fecha" : "Inicio"} *</label>
                <input type={form.all_day ? "date" : "datetime-local"} style={inputSx}
                  value={form.all_day ? form.scheduled_start?.slice(0, 10) || "" : form.scheduled_start || ""}
                  onChange={e => setForm(f => ({ ...f, scheduled_start: e.target.value }))} />
              </div>

              {!form.all_day && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelSx}>Fin (opcional)</label>
                    <input type="datetime-local" style={inputSx} value={form.scheduled_end || ""} onChange={e => setForm(f => ({ ...f, scheduled_end: e.target.value }))} />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelSx}>Duración estimada (hs)</label>
                    <input type="number" min={0.5} max={24} step={0.5} style={inputSx} placeholder="Ej: 2.5" value={form.estimated_duration_hs} onChange={e => setForm(f => ({ ...f, estimated_duration_hs: e.target.value }))} />
                    <div style={{ fontSize: 10, color: "#aab8c2", marginTop: 3 }}>Si ingresás duración sin fecha de fin, se calcula automáticamente</div>
                  </div>
                </>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={labelSx}>Notas internas</label>
                <textarea style={{ ...inputSx, minHeight: 80, resize: "vertical", lineHeight: 1.6 }} placeholder="Recordatorios, materiales, instrucciones de acceso..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>

              {error && <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 12, fontWeight: 600 }}>⚠ {error}</div>}

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
                <button onClick={handleSave} disabled={loading} style={{ flex: 2, padding: "11px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: loading ? "not-allowed" : "pointer" }}>
                  {loading ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Block Modal ──────────────────────────────────────────────────────────────
function BlockModal({ block, onClose, onSave, onDelete }) {
  const isNew = !block;
  const [form, setForm] = useState({ starts_at: block?.starts_at ? new Date(block.starts_at).toISOString().slice(0, 10) : "", ends_at: block?.ends_at ? new Date(block.ends_at).toISOString().slice(0, 10) : "", reason: block?.reason || "Personal", all_day: block?.all_day ?? true });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleSave = async () => {
    if (!form.starts_at || !form.ends_at) { setError("Las fechas son requeridas"); return; }
    setError(""); setLoading(true);
    try {
      const starts = new Date(form.starts_at); starts.setHours(0, 0, 0, 0);
      const ends   = new Date(form.ends_at);   ends.setHours(23, 59, 59, 999);
      await api.post("/providers/calendar/blocks", { starts_at: starts.toISOString(), ends_at: ends.toISOString(), reason: form.reason, all_day: form.all_day });
      onSave(); onClose();
    } catch (err) { setError(err.response?.data?.error || "Error al crear bloqueo"); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setLoading(true);
    try { await api.delete(`/providers/calendar/blocks/${block.id}`); onDelete(); onClose(); }
    catch (err) { setError(err.response?.data?.error || "Error al eliminar"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, width: "min(440px, 100%)", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        <div style={{ background: "#c0392b", padding: "18px 22px", borderRadius: "18px 18px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>🚫 {isNew ? "Bloquear días" : "Editar bloqueo"}</div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        <div style={{ padding: "20px 22px" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelSx}>Motivo</label>
            <select style={{ ...inputSx, cursor: "pointer" }} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}>
              {BLOCK_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={labelSx}>Desde *</label>
              <input type="date" style={inputSx} value={form.starts_at} onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelSx}>Hasta *</label>
              <input type="date" style={inputSx} value={form.ends_at} onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))} />
            </div>
          </div>

          {error && <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 12, fontWeight: 600 }}>⚠ {error}</div>}

          <div style={{ display: "flex", gap: 10 }}>
            {!isNew && (
              <button onClick={handleDelete} disabled={loading} style={{ padding: "11px 16px", borderRadius: 50, border: "none", background: "rgba(192,57,43,0.1)", color: "#c0392b", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Eliminar
              </button>
            )}
            <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
            <button onClick={handleSave} disabled={loading} style={{ flex: 2, padding: "11px", borderRadius: 50, border: "none", background: "#c0392b", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: loading ? "not-allowed" : "pointer" }}>
              {loading ? "..." : isNew ? "Bloquear días" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderCalendarPage() {
  const [weekStart,   setWeekStart]   = useState(() => getMondayOf(new Date()));
  const [calData,     setCalData]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [blockModal,  setBlockModal]  = useState(false);

  const fetchWeek = useCallback(() => {
    setLoading(true); setError("");
    const iso = weekStart.toISOString().slice(0, 10);
    api.get(`/providers/calendar/week?week_start=${iso}`)
      .then(res => setCalData(res.data))
      .catch(() => setError("No se pudo cargar el calendario."))
      .finally(() => setLoading(false));
  }, [weekStart]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const jobsByDay = (calData?.jobs || []).reduce((acc, job) => {
    if (!job.scheduled_start) { (acc["unscheduled"] = acc["unscheduled"] || []).push(job); return acc; }
    // "Todo el día" es una fecha de calendario: no se convierte de zona
    const key = (job.all_day ? fechaLocal(job.scheduled_start) : new Date(job.scheduled_start)).toDateString();
    (acc[key] = acc[key] || []).push(job);
    return acc;
  }, {});

  const blocksByDay = (calData?.blocks || []).reduce((acc, block) => {
    const start = new Date(block.starts_at), end = new Date(block.ends_at);
    days.forEach(day => {
      if (day >= start && day <= end) { const key = day.toDateString(); (acc[key] = acc[key] || []).push(block); }
    });
    return acc;
  }, {});

  const availByDay  = (calData?.availability || []).reduce((acc, a) => { acc[a.day_of_week] = a; return acc; }, {});
  const totalJobs   = (calData?.jobs || []).length;
  const pendingJobs = (calData?.jobs || []).filter(j => j.status === "scheduled").length;
  const inProgress  = (calData?.jobs || []).filter(j => j.status === "in_progress").length;
  const unscheduled = jobsByDay["unscheduled"] || [];

  const monthLabel = (() => {
    const sm = MONTHS_ES[weekStart.getMonth()];
    const ed = addDays(weekStart, 6);
    const em = MONTHS_ES[ed.getMonth()];
    const y  = weekStart.getFullYear();
    return sm === em ? `${sm} ${y}` : `${sm} / ${em} ${y}`;
  })();

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "28px 24px 60px" }}>
        <div style={{ maxWidth: 1060, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: "#163244" }}>Mi agenda</h1>
              <p style={{ margin: 0, color: "#7a93a4", fontSize: 13 }}>
                {totalJobs} trabajo{totalJobs !== 1 ? "s" : ""} esta semana
                {pendingJobs > 0 && ` · ${pendingJobs} agendado${pendingJobs !== 1 ? "s" : ""}`}
                {inProgress  > 0 && ` · ${inProgress} en curso`}
              </p>
            </div>
            <button onClick={() => setBlockModal(null)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 50, background: "rgba(192,57,43,0.08)", border: "1.5px solid rgba(192,57,43,0.2)", color: "#c0392b", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              🚫 Bloquear días
            </button>
          </div>

          {/* Navegación semana */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, background: "#fff", borderRadius: 12, padding: "10px 14px", border: "1px solid rgba(0,0,0,0.07)" }}>
            <button onClick={() => setWeekStart(d => addDays(d, -7))} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#163244" }}>{monthLabel}</div>
              <div style={{ fontSize: 11, color: "#7a93a4", marginTop: 1 }}>{formatDate(weekStart)} — {formatDate(addDays(weekStart, 6))}</div>
            </div>
            <button onClick={() => setWeekStart(getMondayOf(new Date()))} style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid rgba(22,50,68,0.2)", background: "transparent", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>Hoy</button>
            <button onClick={() => setWeekStart(d => addDays(d, 7))} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: "center", padding: "60px", color: "#7a93a4" }}>
              <span style={{ display: "inline-block", width: 26, height: 26, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} /><br/>
              Cargando calendario...
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: 14, color: "#c0392b", fontSize: 14 }}>⚠ {error}</div>
          )}

          {/* Grilla semanal */}
          {!loading && !error && calData && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: 16 }}>
                {days.map((day, idx) => {
                  const dayKey    = day.toDateString();
                  const dayJobs   = jobsByDay[dayKey]   || [];
                  const dayBlocks = blocksByDay[dayKey] || [];
                  const avail     = availByDay[day.getDay()];
                  const today     = isToday(day);
                  const isAvail   = avail?.available ?? false;

                  return (
                    <div key={idx} style={{
                      background: today ? "#163244" : "#fff",
                      borderRadius: 12,
                      border: today ? "none" : "1px solid rgba(0,0,0,0.07)",
                      minHeight: 160, overflow: "hidden",
                      boxShadow: today ? "0 4px 16px rgba(22,50,68,0.15)" : "0 1px 4px rgba(22,50,68,0.04)",
                    }}>
                      {/* Cabecera día */}
                      <div style={{ padding: "8px 10px", borderBottom: `1px solid ${today ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`, textAlign: "center", background: today ? "transparent" : isAvail ? "#f8fafb" : "#f5f5f5" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: today ? "#E7A432" : "#aab8c2" }}>
                          {DAYS_ES[day.getDay()].toUpperCase()}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 900, color: today ? "#fff" : isAvail ? "#163244" : "#ccc", lineHeight: 1.2 }}>
                          {day.getDate()}
                        </div>
                        {isAvail && avail && (
                          <div style={{ fontSize: 8, color: today ? "rgba(255,255,255,0.5)" : "#aab8c2", marginTop: 1 }}>
                            {avail.start_time.slice(0,5)}–{avail.end_time.slice(0,5)}
                          </div>
                        )}
                        {!isAvail && <div style={{ fontSize: 8, color: today ? "rgba(255,255,255,0.35)" : "#ccc" }}>No disponible</div>}
                      </div>

                      {/* Contenido */}
                      <div style={{ padding: "6px 6px" }}>
                        {dayBlocks.map(block => <BlockChip key={block.id} block={block} onClick={() => setBlockModal(block)} />)}
                        {dayJobs.map(job => <JobCard key={job.id} job={job} onClick={setSelectedJob} />)}
                        {dayJobs.length === 0 && dayBlocks.length === 0 && isAvail && (
                          <div style={{ fontSize: 9, color: today ? "rgba(255,255,255,0.25)" : "#dce5ec", textAlign: "center", padding: "14px 0" }}>Libre</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Jobs sin fecha */}
              {unscheduled.length > 0 && (
                <div style={{ background: "#fff", borderRadius: 12, border: "1.5px dashed #E7A432", padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#b06a00", letterSpacing: "0.06em", marginBottom: 10 }}>
                    ⏳ SIN FECHA CONFIRMADA ({unscheduled.length})
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {unscheduled.map(job => (
                      <div key={job.id} onClick={() => setSelectedJob(job)} style={{ background: "rgba(231,164,50,0.08)", border: "1.5px solid rgba(231,164,50,0.3)", borderRadius: 9, padding: "8px 12px", cursor: "pointer", fontSize: 12 }}>
                        <div style={{ fontWeight: 700, color: "#163244" }}>{SERVICE_LABELS[job.service_key] || job.service_key}</div>
                        <div style={{ color: "#7a93a4", fontSize: 11 }}>{job.client_name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leyenda */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, color: "#7a93a4" }}>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot }} />
                    {cfg.label}
                  </div>
                ))}
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#c0392b" }} />
                  Bloqueado
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedJob && <JobModal job={selectedJob} onClose={() => setSelectedJob(null)} onSave={fetchWeek} onStatusChange={fetchWeek} />}
      {blockModal !== false && <BlockModal block={blockModal} onClose={() => setBlockModal(false)} onSave={fetchWeek} onDelete={fetchWeek} />}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
